import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'

import {
  CAMPAIGN_STATUSES,
  CAMPAIGN_TYPES,
  CampaignFormValues,
  campaignFormSchema,
  campaignToFormValues,
  defaultFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
} from './schema'
import styles from './CampaignFormDialog.module.scss'

import { isApiError } from '@/api'
import { CampaignListItem } from '@/api/generated'
import { createPromoCampaign, updatePromoCampaign } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormField from '@/components/FormField/FormField'
import { CAMPAIGNS_QUERY_KEY } from '@/pages/app/Campaigns'

interface CampaignFormDialogProps {
  isOpen: boolean
  campaign: CampaignListItem | null
  onSave: () => void
  onCancel: () => void
}

const CampaignFormDialog = ({ isOpen, campaign, onSave, onCancel }: CampaignFormDialogProps) => {
  const isEditMode = !!campaign
  const [formError, setFormError] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const queryClient = useQueryClient()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: defaultFormValues,
  })

  useEffect(() => {
    if (isOpen) {
      reset(campaign ? campaignToFormValues(campaign) : defaultFormValues)
      setFormError(null)
      setShowAdvanced(isEditMode)
    }
  }, [isOpen, campaign, reset, isEditMode])

  const saveMutation = useMutation({
    mutationFn: async (formValues: CampaignFormValues) => {
      if (isEditMode && campaign) {
        return updatePromoCampaign(campaign.id, formValuesToUpdatePayload(formValues))
      }
      return createPromoCampaign(formValuesToCreatePayload(formValues))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY })
      reset()
      onSave()
    },
    onError: (err) => {
      if (isApiError(err)) {
        const detail = typeof err.detail === 'string' ? err.detail : 'Operation failed'
        setFormError(detail)
      } else {
        setFormError('An unexpected error occurred')
      }
    },
  })

  const handleCancel = () => {
    setFormError(null)
    reset()
    onCancel()
  }

  const onSubmit = (formValues: CampaignFormValues) => {
    setFormError(null)
    saveMutation.mutate(formValues)
  }

  return (
    <DialogWrapper
      isOpen={isOpen}
      title={isEditMode ? 'Edit Campaign' : 'Create Campaign'}
      close={handleCancel}
      minWidth={500}
    >
      <form className={styles.dialogBody} onSubmit={handleSubmit(onSubmit)}>
        {formError && <div className={styles.errorBanner}>{formError}</div>}

        <FormField
          name="name"
          label="Name"
          control={control}
          error={errors.name}
          placeholder="e.g. TechCrunch Launch"
          disabled={saveMutation.isPending}
        />

        <FormField
          name="sourceName"
          label="Source Name"
          control={control}
          error={errors.sourceName}
          placeholder="e.g. Alex Banks"
          disabled={saveMutation.isPending}
        />

        <FormField
          name="trialDays"
          label="Trial Days"
          type="number"
          control={control}
          error={errors.trialDays}
          disabled={saveMutation.isPending}
        />

        {isEditMode && (
          <div className={styles.formGroup}>
            <label className={styles.label}>Status</label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <select className={styles.select} {...field} disabled={saveMutation.isPending}>
                  {CAMPAIGN_STATUSES.map((campaignStatus) => (
                    <option key={campaignStatus} value={campaignStatus}>
                      {campaignStatus}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        )}

        <button
          type="button"
          className={styles.advancedToggle}
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          <span className={clsx(styles.advancedArrow, showAdvanced && styles.advancedArrowOpen)}>
            &#9654;
          </span>
          Advanced settings
        </button>

        {showAdvanced && (
          <div className={styles.advancedSection}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Type</label>
              <Controller
                name="campaignType"
                control={control}
                render={({ field }) => (
                  <select className={styles.select} {...field} value={field.value ?? 'sponsor'} disabled={saveMutation.isPending}>
                    {CAMPAIGN_TYPES.map((campaignType) => (
                      <option key={campaignType} value={campaignType}>
                        {campaignType}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <FormField
              name="attributionMessage"
              label="Attribution Message"
              control={control}
              placeholder="e.g. Enjoy 3 months free from Alex Banks"
              required={false}
              disabled={saveMutation.isPending}
            />

            <FormField
              name="maxRedemptions"
              label="Max Redemptions"
              type="number"
              control={control}
              placeholder="Unlimited"
              required={false}
              disabled={saveMutation.isPending}
            />

            <FormField
              name="startsAt"
              label="Starts At"
              type="date"
              control={control}
              required={false}
              disabled={saveMutation.isPending}
            />

            <FormField
              name="expiresAt"
              label="Expires At"
              type="date"
              control={control}
              required={false}
              disabled={saveMutation.isPending}
            />

            <FormField
              name="description"
              label="Description"
              type="textarea"
              control={control}
              placeholder="Internal notes about this campaign..."
              required={false}
              disabled={saveMutation.isPending}
            />
          </div>
        )}

        <div className={styles.dialogFooter}>
          <Button title="Cancel" onClick={handleCancel} type="light" />
          <Button
            title={saveMutation.isPending ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save' : 'Create'}
            onClick={handleSubmit(onSubmit)}
            type="dark"
            isDisabled={saveMutation.isPending}
          />
        </div>
      </form>
    </DialogWrapper>
  )
}

export default CampaignFormDialog
